/**
 * How the apps interpret the vendor neutral consent manager.
 *
 * `consentManager.ts` only stores a decision; this module holds the product rules on top of it, so
 * every consumer gates on the same condition — whether it is a user-scoped advertising identifier
 * (PPID, advid) or any other consent category.
 *
 * The manager's lifecycle is owned by the CMP provider (the OneTrust CTV provider in
 * dal/pub/snowpiercer/consent/OneTrustCTV.tsx on Smart TV, Cookiebot in
 * shared/libs/offspring/libs/cookiebotConsent.ts on the web), which also covers the
 * kill-switch-off case by recording "integration disabled, everything granted".
 */
import {
  type ConsentCategory,
  getCurrentConsentDecision,
  isConsentIntegrationEnabled,
} from './consentManager';

/**
 * Whether a category may be acted on.
 *
 * There are only two answers. A missing decision is not a third state to observe: until a CMP says
 * a category is granted, it counts as declined, so the manager fails closed on anything it has not
 * been told. When the CMP is disabled, pre-CMP behavior allows every category.
 *
 * Server renders always read as declined. The manager is a module singleton, and on the server that
 * singleton is shared across every request, so it can never hold one visitor's answer — the only
 * safe answer during SSR is "not granted". Consent-dependent output therefore belongs on the
 * client, where the CMP has actually spoken.
 */
export function isConsentCategoryAllowed(category: ConsentCategory): boolean {
  if (typeof window === 'undefined') return false;
  if (!isConsentIntegrationEnabled()) return true;
  return getCurrentConsentDecision()?.[category] === true;
}

/**
 * Whether the Marketing category is granted. Note: this is NOT the advid/PPID rule — advertising
 * identifiers require FULL consent of every category in the CMP template, exposed through
 * `areAdvertisingIdentifiersAllowed()` on the consent manager.
 */
export function isMarketingAllowed(): boolean {
  return isConsentCategoryAllowed('Marketing');
}
